<div class="calendar-wrapper" [class.booksy-dragging-active]="dragGhost">
  <div class="calendar-header">
    <div class="header-buttons">
      <button class="btn-nav" (click)="previous()">‹</button>
      <button class="btn-today" (click)="today()">Hoy</button>
      <button class="btn-nav" (click)="next()">›</button>
    </div>

    <div class="calendar-title">{{ viewTitle | titlecase }}</div>

    <div class="view-switch">
      <button [class.active]="view === CalendarView.Month" (click)="onViewChange(CalendarView.Month)">Mes</button>
      <button [class.active]="view === CalendarView.Week" (click)="onViewChange(CalendarView.Week)">Semana</button>
      <button [class.active]="view === CalendarView.Day" (click)="onViewChange(CalendarView.Day)">Día</button>
    </div>
  </div>

  <div class="calendar-filters">
    <div class="filter-item">
      <app-multi-select-autocomplete 
        [control]="merchantLocationsControl" 
        [config]="merchantLocationsConfig">
      </app-multi-select-autocomplete>
    </div>
    <div class="filter-item">
      <app-multi-select-autocomplete 
        [control]="employeesControl" 
        [config]="employeesConfig">
      </app-multi-select-autocomplete>
    </div>
  </div>


  <div [ngSwitch]="view">
    <mwl-calendar-month-view *ngSwitchCase="CalendarView.Month" [viewDate]="viewDate" [events]="events"
      [cellTemplate]="customMonthCellTemplate" (dayClicked)="onDayClicked($event.day)"
      (eventClicked)="onEventClicked($event)" (eventTimesChanged)="onEventTimesChanged($event)">
    </mwl-calendar-month-view>

    <div *ngSwitchCase="CalendarView.Week" class="booksy-week-view">
      <!-- Day headers row -->
      <div class="booksy-week-header">
        <div class="booksy-week-employee-corner"></div>
        <div class="booksy-week-day-header"
          *ngFor="let day of weekDays; trackBy: trackByDay"
          [class.booksy-week-day-today]="isWeekDayToday(day)">
          <span class="booksy-week-day-name">{{ formatWeekDayName(day) }}</span>
          <span class="booksy-week-day-num" [class.booksy-day-num-today]="isWeekDayToday(day)">{{ formatWeekDayNum(day) }}</span>
        </div>
      </div>

      <!-- Employee rows -->
      <div class="booksy-week-body">
        <div class="booksy-week-row" *ngFor="let employee of visibleEmployees; trackBy: trackByEmployee">

          <!-- Employee label cell -->
          <div class="booksy-week-employee-cell">
            <ng-container *ngIf="getEmployeeAvatarUrl(employee) as avatar; else weekFallbackAvatar">
              <img class="booksy-week-avatar" [src]="avatar" [alt]="employee.display" />
            </ng-container>
            <ng-template #weekFallbackAvatar>
              <div class="booksy-week-avatar booksy-week-avatar-fallback">{{ getEmployeeInitials(employee) }}</div>
            </ng-template>
            <span class="booksy-week-employee-name">{{ employee.display }}</span>
          </div>

          <!-- One cell per day -->
          <div class="booksy-week-day-cell"
            *ngFor="let day of weekDays; trackBy: trackByDay"
            [attr.data-employee-id]="employee.id"
            [attr.data-day-key]="getWeekDayKey(day)"
            [class.booksy-week-day-cell-today]="isWeekDayToday(day)"
            [class.booksy-week-day-cell-drop-target]="weekDragState && weekDragState.previewEmployeeId === employee.id && weekDragState.previewDayKey === getWeekDayKey(day)"
            (click)="onWeekCellClick(day, employee)">

            <button
              *ngFor="let event of getWeekDayEvents(employee.id, day); trackBy: trackByIndex"
              type="button"
              class="booksy-week-event-card"
              [class.booksy-week-event-card--dragging]="weekDragState && weekDragState.event === event"
              [style.borderLeftColor]="event.color?.primary"
              [style.backgroundColor]="event.color?.secondary"
              (pointerdown)="onWeekEventMouseDown($event, event, employee.id, day)"
              (click)="$event.stopPropagation(); onEventClicked({ event: event })">
              <span class="booksy-week-event-time">{{ formatEventTimeRange(event) }}</span>
              <span class="booksy-week-event-contact">{{ getEventContactName(event) }}</span>
            </button>

            <span *ngIf="getWeekDayEvents(employee.id, day).length === 0" class="booksy-week-add-hint">+</span>
          </div>
        </div>

        <div *ngIf="visibleEmployees.length === 0" class="booksy-week-no-employees">
          No hay empleados disponibles para esta semana.
        </div>
      </div>
    </div>

    <div *ngSwitchCase="CalendarView.Day" class="booksy-day-view">
      <div class="booksy-day-header">
        <div class="booksy-time-corner">
          <span>{{ viewDate | date:'d MMM' }}</span>
        </div>

        <div class="booksy-employee-headers" [style.minWidth.px]="dayColumnsWidth">
          <div *ngIf="!visibleEmployees.length" class="booksy-empty-header">
            No hay empleados disponibles
          </div>

          <div
            *ngFor="let employee of visibleEmployees; trackBy: trackByEmployee"
            class="booksy-employee-header"
            [style.minWidth.px]="employeeColumnWidth"
            [title]="employee.display"
          >
            <ng-container *ngIf="getEmployeeAvatarUrl(employee) as employeeAvatar; else fallbackAvatar">
              <img class="booksy-employee-avatar" [src]="employeeAvatar" [alt]="employee.display" />
            </ng-container>

            <ng-template #fallbackAvatar>
              <div class="booksy-employee-avatar booksy-employee-avatar-fallback">
                {{ getEmployeeInitials(employee) }}
              </div>
            </ng-template>

            <div class="booksy-employee-text">
              <div class="booksy-employee-name">{{ employee.display }}</div>
              <div class="booksy-employee-subtitle">Agenda del día</div>
            </div>
          </div>
        </div>
      </div>

      <div class="booksy-day-body">
        <div class="booksy-time-column">
          <div
            *ngFor="let hourLabel of hourLabels; trackBy: trackByMarker"
            class="booksy-time-label"
            [style.top.px]="hourLabel.top"
          >
            {{ hourLabel.label }}
          </div>
        </div>

        <div class="booksy-columns-wrapper" [style.minWidth.px]="dayColumnsWidth">
          <div class="booksy-grid-lines" [style.height.px]="dayBodyHeight">
            <div
              *ngFor="let marker of timelineMarkers; trackBy: trackByMarker"
              class="booksy-grid-line"
              [class.booksy-grid-line-half]="!marker.isHour"
              [style.top.px]="marker.top"
            ></div>
          </div>

          <div
            *ngIf="currentTimeIndicatorTop !== null"
            class="booksy-current-time-line"
            [style.top.px]="currentTimeIndicatorTop"
          >
            <span class="booksy-current-time-dot"></span>
          </div>

          <ng-container *ngIf="visibleEmployees.length; else emptyDayState">
            <div
              *ngFor="let employee of visibleEmployees; trackBy: trackByEmployee"
              class="booksy-employee-column"
              [style.minWidth.px]="employeeColumnWidth"
              [style.height.px]="dayBodyHeight"
              (click)="onDayColumnClick($event, employee)"
            >
              <div
                *ngFor="let eventLayout of eventLayoutsByEmployee[employee.id] || []; trackBy: trackByEventLayout"
                class="booksy-day-event"
                [class.booksy-day-event--dragging]="dayDragState && dayDragState.event === eventLayout.event"
                [style.top.px]="eventLayout.top"
                [style.height.px]="eventLayout.height"
                [style.backgroundColor]="eventLayout.event.color?.secondary"
                [style.borderLeftColor]="eventLayout.event.color?.primary"
                [attr.aria-label]="getEventAriaLabel(eventLayout.event)"
                [attr.tabindex]="0"
                role="button"
                (pointerdown)="onDayEventMouseDown($event, eventLayout, 'move')"
                (click)="$event.stopPropagation(); onEventClicked({ event: eventLayout.event })"
                (keydown)="onDayEventKeydown($event, eventLayout.event)"
              >
                <div class="booksy-day-event-resize-handle booksy-day-event-resize-handle--top"
                     (pointerdown)="onDayEventMouseDown($event, eventLayout, 'resize-top')"
                     (click)="$event.stopPropagation()"></div>
                <span class="booksy-day-event-time">{{ formatEventTimeRange(eventLayout.event) }}</span>
                <span class="booksy-day-event-contact">{{ getEventContactName(eventLayout.event) }}</span>
                <span class="booksy-day-event-title">{{ getEventServiceName(eventLayout.event) }}</span>
                <div class="booksy-day-event-resize-handle booksy-day-event-resize-handle--bottom"
                     (pointerdown)="onDayEventMouseDown($event, eventLayout, 'resize-bottom')"
                     (click)="$event.stopPropagation()"></div>
              </div>
            </div>
          </ng-container>

          <ng-template #emptyDayState>
            <div class="booksy-empty-state" [style.height.px]="dayBodyHeight">
              No hay empleados o citas para la fecha seleccionada.
            </div>
          </ng-template>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Fantasma de arrastre: tarjeta flotante que sigue al cursor -->
<div *ngIf="dragGhost"
  class="booksy-drag-ghost"
  [style.left.px]="dragGhost.x"
  [style.top.px]="dragGhost.y"
  [style.width.px]="dragGhost.width"
  [style.borderLeftColor]="dragGhost.primary"
  [style.backgroundColor]="dragGhost.secondary">
  <span class="booksy-drag-ghost-time">{{ dragGhost.time }}</span>
  <span class="booksy-drag-ghost-contact">{{ dragGhost.contact }}</span>
</div>

<ng-template #customMonthCellTemplate let-day="day">
  <div class="cal-cell-top">
    <span class="cal-day-number">{{ day.date | date:'d' }}</span>
  </div>

  <div class="cal-events month-events-list">
    <button
      *ngFor="let event of getMonthVisibleEvents(day); trackBy: trackByIndex"
      type="button"
      class="month-mini-event"
      [style.backgroundColor]="event.color?.secondary"
      [style.borderLeftColor]="event.color?.primary"
      [attr.aria-label]="getEventAriaLabel(event)"
      (click)="$event.stopPropagation(); onEventClicked({ event: event })">
      <span class="month-mini-event-time">{{ formatMonthEventStart(event) }}</span>
      <span class="month-mini-event-contact">{{ getEventContactName(event) }}</span>
    </button>

    <button
      *ngIf="getMonthHiddenEventsCount(day) > 0"
      type="button"
      class="month-more-events"
      (click)="toggleMonthDayExpanded(day, $event)">
      +{{ getMonthHiddenEventsCount(day) }} más
    </button>

    <div *ngIf="isMonthDayExpanded(day)" class="month-hidden-events-panel">
      <button
        *ngFor="let event of getMonthHiddenEvents(day); trackBy: trackByIndex"
        type="button"
        class="month-mini-event month-mini-event-hidden"
        [style.backgroundColor]="event.color?.secondary"
        [style.borderLeftColor]="event.color?.primary"
        [attr.aria-label]="getEventAriaLabel(event)"
        (click)="$event.stopPropagation(); onEventClicked({ event: event })">
        <span class="month-mini-event-time">{{ formatMonthEventStart(event) }}</span>
        <span class="month-mini-event-contact">{{ getEventContactName(event) }}</span>
      </button>
    </div>
  </div>
</ng-template>
